import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTenants } from '../tenants';
import { FiltrosDeComprobantes } from './FiltrosDeComprobantes';
import { ResumenDelPeriodo } from './ResumenDelPeriodo';
import { TablaDeComprobantes } from './TablaDeComprobantes';
import { useComprobantes } from './consultas';
import type { FiltroLocal } from './filtros';
import { FILTRO_INICIAL, aFiltroDeApi, enPagina, puedeConsultar, rangoInvertido } from './filtros';
import { mensajeDeConsulta } from './mensajes';

/**
 * Listado de comprobantes emitidos (KAN-146). Ruta `/comprobantes`, dentro de
 * la sesión y **sin `RequiereRol`**: un `OPERADOR` que emite tiene que poder
 * mirar lo que emitió. Quién ve qué lo decide el backend por credencial — una
 * persona ve todos los tenants, un **sistema conectado solo su lista blanca**,
 * también al leer.
 *
 * # PARA QUÉ EXISTE
 *
 * Hasta acá, después de emitir no había forma de mirar lo emitido salvo entrar
 * a la base a mano: un CAE que se perdió en la respuesta HTTP no se recuperaba
 * de ningún lado, y cuando la reconciliación (KAN-89) marca un desfase no había
 * dónde ir a mirar.
 *
 * # SOLO LECTURA, SIN EXCEPCIONES
 *
 * ⚠️ **Nada de esta pantalla escribe.** Ni "reemitir", ni "corregir", ni
 * "borrar". Un comprobante fiscal no se borra: se corrige con una **nota de
 * crédito**, que es una emisión nueva y va por `POST /api/v1/comprobantes` como
 * cualquier otra. Es la tentación natural de un listado ("agrego un botoncito
 * para reintentar acá") y es exactamente lo que prohíbe la regla de las tres
 * superficies: dos caminos de escritura terminan divergiendo, y acá divergir
 * significa dos comprobantes fiscales por la misma operación.
 *
 * # LOS TENANTS SE PIDEN SIN `soloEmisores`
 *
 * A propósito, y al revés que la pantalla de emisión: un tenant dado de baja, o
 * con la delegación revocada, **igual tiene historia**. Filtrarlo acá haría
 * desaparecer del selector justo al contribuyente cuya facturación alguien
 * necesita revisar después de darlo de baja.
 */
export function PantallaDeComprobantes() {
  const tenants = useTenants();
  const [filtro, setFiltro] = useState<FiltroLocal>(FILTRO_INICIAL);

  const habilitada = puedeConsultar(filtro);
  const listado = useComprobantes(aFiltroDeApi(filtro), habilitada);
  const pagina = listado.data;

  return (
    <main>
      <p>
        <Link to="/">Inicio</Link>
      </p>
      <h1>Comprobantes emitidos</h1>
      <p className="nota">
        Lo que quedó registrado de cada emisión, tal como viajó a ARCA. Esta pantalla solo consulta:
        un comprobante emitido no se edita ni se borra, se corrige emitiendo una nota de crédito.
      </p>

      {tenants.isError ? (
        <p role="alert" className="error">
          No se pudo cargar la lista de contribuyentes. Sin eso no se puede elegir de quién ver los
          comprobantes: el listado siempre es de un contribuyente, nunca de todos mezclados.
        </p>
      ) : null}

      <FiltrosDeComprobantes tenants={tenants.data ?? []} filtro={filtro} alCambiar={setFiltro} />

      {rangoInvertido(filtro) ? (
        <p role="alert" className="error">
          La fecha "Desde" es posterior a la de "Hasta". Corregí el rango: así no se puede buscar
          nada.
        </p>
      ) : null}

      {filtro.tenantId === null ? (
        <p role="status">Elegí un contribuyente para ver sus comprobantes.</p>
      ) : null}

      {habilitada && listado.isPending ? <p role="status">Buscando comprobantes…</p> : null}

      {listado.isError ? (
        <p role="alert" className="error">
          {mensajeDeConsulta(listado.error, 'listado')}
        </p>
      ) : null}

      {pagina ? (
        pagina.total === 0 ? (
          // "No hay ninguno CON ESTOS FILTROS", nunca "no facturó nada": lo
          // segundo sería una afirmación sobre la facturación de un
          // contribuyente que este filtro no autoriza a hacer.
          <p role="status">No hay comprobantes que cumplan estos filtros.</p>
        ) : (
          <>
            <TablaDeComprobantes filas={pagina.contenido} />
            <Paginacion
              pagina={pagina.pagina}
              totalPaginas={pagina.totalPaginas}
              total={pagina.total}
              alIr={(numero) => setFiltro(enPagina(filtro, numero))}
            />
          </>
        )
      ) : null}

      <ResumenDelPeriodo tenantId={filtro.tenantId} desde={filtro.desde} hasta={filtro.hasta} />
    </main>
  );
}

/**
 * Anterior / siguiente, con la cuenta a la vista.
 *
 * Los números que muestra salen de la **respuesta**, no del estado local: el
 * `tamano` puede venir recortado por el backend y `total`/`totalPaginas`
 * describen el filtro entero, no esta página. Mostrar lo que se pidió en vez de
 * lo que se recibió sería mostrar una página que quizá no es la que está abajo.
 */
function Paginacion({
  pagina,
  totalPaginas,
  total,
  alIr,
}: {
  pagina: number;
  totalPaginas: number;
  total: number;
  alIr: (pagina: number) => void;
}) {
  return (
    <p>
      <button type="button" disabled={pagina <= 0} onClick={() => alIr(pagina - 1)}>
        Anterior
      </button>{' '}
      <span>
        Página {pagina + 1} de {totalPaginas} ({total} comprobantes)
      </span>{' '}
      <button
        type="button"
        disabled={pagina + 1 >= totalPaginas}
        onClick={() => alIr(pagina + 1)}
      >
        Siguiente
      </button>
    </p>
  );
}
